import cn from 'classnames'
import { useConfirm } from 'legacy/lut/components/shared/Confirm/useConfirm'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { FC, useCallback, useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useSelector } from 'react-redux'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import {
  cancelEditingGroup,
  changeEditingGroupId,
  changeGroupOrder,
} from 'partner360/features/assortment/assortmentSlice'

import { IAssortmentGroup } from '../../../../features/assortment/models/data/IAssortmentGroup'
import { TRANSLATION_KEYS } from '../../translations'
import { editConfirmDialogIdentifier } from '../editConfirmDialog/EditConfirmDialog'

import { GroupDetails } from './components/groupDetails/GroupDetails'
import { GroupName } from './components/groupName/GroupName'

import styles from './AssortmentGroup.module.scss'

interface IAssortmentGroupProps {
  disableDragging?: boolean
  isEditingGroup: boolean
  card: IAssortmentGroup
  index: number
  onSave: () => void
  onSaveOrder: () => void
  onDeleteCard: (card: IAssortmentGroup) => void
  onResetOrder?: (itemId: number, index: number) => void
}

export const AssortmentGroup: FC<IAssortmentGroupProps> = ({
  disableDragging = false,
  isEditingGroup,
  card,
  index,
  onSave,
  onSaveOrder,
  onDeleteCard,
  onResetOrder,
}) => {
  const itemRef = useRef(null)
  const dispatch = useAppDispatch()
  const isEditMode = useSelector(selectPublishingMode)
  const { confirm } = useConfirm()

  const [, drag] = useDrag({
    type: 'ASSORTMENT_GROUP',
    item: {
      index,
      itemId: card.EntryId,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    // canDrag: (monitor) => {
    //   // if (disableDragging) {
    //   //   return false
    //   // }

    //   return monitor.canDrag()
    // },
    end: (droppedItem, monitor) => {
      const { itemId, index } = droppedItem
      if (!monitor.didDrop() && onResetOrder) {
        onResetOrder(itemId, index)
      }
    },
  })
  const [{ handlerId }, drop] = useDrop<
    { itemId: number; index: number },
    void,
    { handlerId: any }
  >({
    accept: 'ASSORTMENT_GROUP',
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
    }),
    hover: (draggedItem) => {
      if (draggedItem.itemId !== card.EntryId) {
        dispatch(
          changeGroupOrder({
            itemId: draggedItem.itemId,
            targetIndex: index,
          })
        )
      }
    },
    drop: () => {
      onSaveOrder()
      // save changes on drop
    },
  })

  drag(drop(itemRef))

  const editGroup = useCallback(() => {
    dispatch(changeEditingGroupId(card.EntryId))
  }, [dispatch, card.EntryId])

  const handleCancelEditing = useCallback(async () => {
    if (card.isChanged && (await confirm(editConfirmDialogIdentifier))) {
      // saving changes logic
      await onSave()
    }

    dispatch(cancelEditingGroup())
  }, [dispatch, onSave, card.isChanged])

  return (
    <div className={styles.root} ref={itemRef} data-handler-id={handlerId}>
      <div
        className={cn(styles.content, {
          [styles.editMode]: isEditingGroup,
        })}
      >
        <div
          className={cn(styles.actionControls, {
            [styles.readMode]: !isEditingGroup,
          })}
        >
          <RenderWhen condition={isEditMode && !isEditingGroup}>
            <div className={styles.row}>
              <Tooltip
                tipContent={() => (
                  <Translation text={TRANSLATION_KEYS.group.tooltips.editTooltip} />
                )}
              >
                <Button
                  color="grey"
                  className={styles.actionButton}
                  variant="text"
                  iconButton="kbase kbase-edit"
                  onClick={editGroup}
                />
              </Tooltip>
            </div>
          </RenderWhen>
          <RenderWhen condition={isEditingGroup}>
            <Tooltip
              tipContent={() => (
                <Translation text={TRANSLATION_KEYS.group.tooltips.cancelTooltip} />
              )}
            >
              <Button
                color="grey"
                className={styles.actionButton}
                variant="text"
                iconButton="kbase kbase-close"
                onClick={handleCancelEditing}
              />
            </Tooltip>
            <Tooltip
              tipContent={() => (
                <Translation text={TRANSLATION_KEYS.group.tooltips.deleteTooltip} />
              )}
            >
              <Button
                color="grey"
                className={styles.actionButton}
                variant="text"
                iconButton="kbase kbase-delete"
                onClick={() => onDeleteCard(card)}
              />
            </Tooltip>
            <Tooltip
              tipContent={() => <Translation text={TRANSLATION_KEYS.group.tooltips.saveTooltip} />}
            >
              <Button
                color="grey"
                className={styles.actionButton}
                variant="text"
                iconButton="kbase kbase-save"
                onClick={onSave}
                disabled={!card.isChanged}
              />
            </Tooltip>
          </RenderWhen>
        </div>
        <div className={cn(styles.row)}>
          <GroupName isEditMode={isEditingGroup} group={card} />
        </div>
        <div className={styles.row}>
          <GroupDetails isEditMode={isEditingGroup} group={card} />
        </div>
      </div>
    </div>
  )
}
